import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { LiveOrder } from "@/lib/liveFeed";
import type { RootState } from "..";

/**
 * THE OPERATOR'S OPEN BILLS, kept live.
 *
 * This slice exists because "a staff member can reopen an order they created"
 * needs a list that is CURRENT — an order settled at the till thirty seconds
 * ago must not still be offered as reopenable, and the storefront had no way to
 * learn that. `StaffOrdersDrawer` fetches a flat list on every open and shows
 * receipts; it is a report, not a work list, and it goes stale the moment it
 * paints.
 *
 * The shape stored here is `LiveOrder` — the reduced projection
 * `GET /web/live/:tenantId` streams. The seed comes from
 * `GET /orders/reports/STAFF_ID=<uid>/<tenantId>`, which serves the STORED
 * document (money in CENTS), so the seeding thunk converts; everything the live
 * channel adds is already in major units. One shape in the slice, converted
 * exactly once, at each boundary.
 *
 * ## What "open" means, and why it is not a status
 *
 * `order_settled_at == null` is this platform's definition of an unpaid bill —
 * `:4001` stamps it only once payments cover the total, and the analytics
 * routes already read exactly this as `sale_type=OPEN_ORDERS`. A PAID bill can
 * legitimately still be `PLACED`, so status alone cannot answer it. The two
 * terminal statuses and the soft delete are checked as well, because a
 * cancelled bill is not reopenable either.
 */

export interface StaffOrdersState {
  /** Newest first, the operator's own unsettled bills. */
  data: LiveOrder[];
  loading: boolean;
  /** True while the SSE stream is attached — the floor plan shows a Live pill. */
  live: boolean;
  /** The staff uid this list belongs to; a switch of operator empties it. */
  uid: string | null;
}

const initialState: StaffOrdersState = {
  data: [],
  loading: false,
  live: false,
  uid: null,
};

/** Is this bill still open, i.e. may a round be added to it? */
export const isOpenBill = (order?: LiveOrder | null): boolean => {
  if (!order) return false;
  if (order.is_deleted) return false;
  if (order.order_settled_at != null) return false;
  const status = String(order.status ?? "").toUpperCase();
  return status !== "COMPLETED" && status !== "CANCELLED";
};

/** Did `uid` take this order? Matched on the id AND the mobile, as `:4001` does. */
export const isOwnOrder = (
  order: LiveOrder | null | undefined,
  uid?: string | null,
  phone?: string | null,
): boolean => {
  const taker = order?.order_taker_details;
  if (!taker) return false;
  if (uid && String(taker.staff_id ?? "") === String(uid)) return true;
  // `staffMatcher` on `:4001` resolves an operator by id OR mobile because the
  // two sub-documents disagree across clients. Same question, same answer.
  if (phone && taker.staff_mobile && String(taker.staff_mobile) === String(phone)) return true;
  return false;
};

const byNewest = (a: LiveOrder, b: LiveOrder) =>
  new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();

const staffOrdersSlice = createSlice({
  name: "staffOrders",
  initialState,
  reducers: {
    setStaffOrdersLoading(state, action: PayloadAction<boolean>) {
      state.loading = Boolean(action.payload);
    },
    setStaffOrdersLive(state, action: PayloadAction<boolean>) {
      state.live = Boolean(action.payload);
    },
    /** Replace the whole list — the REST seed, and every re-sync after it. */
    setStaffOrders(
      state,
      action: PayloadAction<{ orders: LiveOrder[]; uid: string | null }>,
    ) {
      state.data = [...action.payload.orders].filter(isOpenBill).sort(byNewest);
      state.uid = action.payload.uid;
      state.loading = false;
    },
    /**
     * Fold ONE live event in. An order that is no longer open drops OUT of the
     * list rather than lingering greyed — which is the whole of "if any order
     * is cleared from the POS it must clear from here".
     */
    applyLiveOrder(state, action: PayloadAction<LiveOrder>) {
      const incoming = action.payload;
      const index = state.data.findIndex((row) => row._id === incoming._id);
      if (!isOpenBill(incoming)) {
        if (index >= 0) state.data.splice(index, 1);
        return;
      }
      if (index >= 0) {
        // Merge, do not replace: a live frame may legitimately omit a field the
        // seed had (the REST seed carries `createdAt`; a settle frame may not).
        state.data[index] = { ...state.data[index], ...incoming };
      } else {
        state.data = [incoming, ...state.data].sort(byNewest);
      }
    },
    removeLiveOrder(state, action: PayloadAction<string>) {
      state.data = state.data.filter((row) => row._id !== action.payload);
    },
    resetStaffOrders() {
      return initialState;
    },
  },
});

export const {
  setStaffOrders,
  setStaffOrdersLoading,
  setStaffOrdersLive,
  applyLiveOrder,
  removeLiveOrder,
  resetStaffOrders,
} = staffOrdersSlice.actions;

/**
 * The operator's OWN open bills.
 *
 * The seed is already scoped by `/orders/reports/STAFF_ID=…`, but the live
 * channel is tenant-wide — every order on the floor arrives here — so the
 * ownership test has to be applied on read as well, or a colleague's new order
 * would appear in this operator's list the moment it was placed.
 */
export const selectMyOpenOrders = createSelector(
  [
    (state: RootState) => state.staffOrders.data,
    // `uid ?? _id`, the same resolution `staffUid()` performs — the stored
    // `tenant_staffs` document has no `uid` field, its own `_id` IS that
    // identity.
    (state: RootState) => state.auth.staff?.uid ?? state.auth.staff?._id ?? null,
    (state: RootState) => state.auth.staff?.phone_number ?? null,
  ],
  (orders, uid, phone) => orders.filter((order) => isOwnOrder(order, uid, phone)),
);

export default staffOrdersSlice.reducer;
